import type { ConfermaAdmin, VoceChecklist } from '@aulaviva/shared';
import { useManuale } from '@/manuale/ManualeProvider';
import { manuale as manualeTesti } from '@/testi/manuale';
import { Alert, Badge, Button, Card, Checkbox, DataTable, Dialog, EmptyState, NativeSelect, StatBlock, StatRow, TabPanel, Tabs, TextArea, useToast, type ColumnDef, HelpButton } from '@aulaviva/ui';
import { Bell, CircleAlert, CircleCheck, ClipboardList, Pencil, Radio, Send, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useAggiornaVoceChecklist, useAzioneWebinar, useChecklist, useConferme, useWebinarAdminDettaglio } from '@/api/admin';
import { formattaDataOra, formattaIntervallo } from '@/lib/date';
import { admin, aula, comuni, documenti as td, presenze as tp, test as tt } from '@/testi';
import { SchedaAttestati, SchedaReport } from './AttestatiAdmin';
import { SchedaTest } from './TestAdmin';
import { useRiepilogoPresenze } from '@/api/presenze';
import { BadgeConferma, BadgeWebinar, CampoRicerca, IntestazioneAdmin, LinkIndietro } from './comuni';

type Azione = 'pubblica' | 'inviti/invia' | 'promemoria/invia';

export function WebinarAdminDettaglio() {
  const t = admin.webinar.dettaglio;
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const scheda = params.get('scheda') ?? 'riepilogo';
  const manualeCtx = useManuale();
  const navigate = useNavigate();
  const toast = useToast();
  const webinar = useWebinarAdminDettaglio(id);
  const azione = useAzioneWebinar(id);
  const [dialogo, setDialogo] = useState<Azione | null>(null);
  const terminato = webinar.data?.stato === 'test_aperto' || webinar.data?.stato === 'chiuso';
  const riepilogoPresenze = useRiepilogoPresenze(id, terminato, 0);

  if (webinar.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (webinar.isError) return <Alert variant="error" title={webinar.error.message} action={<Button onClick={() => webinar.refetch()}>{comuni.riprova}</Button>} />;
  const w = webinar.data;
  const totaleIscritti = w.conteggi.invitati + w.conteggi.confermatiOnline + w.conteggi.confermatiSala + w.conteggi.rinunciati;

  async function esegui(a: Azione) {
    try {
      await azione.mutateAsync(a);
      setDialogo(null);
      toast.mostra({ variant: 'success', title: a === 'pubblica' ? t.pubblicato : t.inviato });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }
  const dialoghi: Record<Azione, { titolo: string; testo: string; conferma: string }> = {
    pubblica: { titolo: t.pubblicaTitolo, testo: t.pubblicaTesto, conferma: t.pubblica },
    'inviti/invia': { titolo: t.invitiTitolo, testo: t.invitiTesto(totaleIscritti), conferma: t.inviaInviti },
    'promemoria/invia': { titolo: t.promemoriaTitolo, testo: t.promemoriaTesto, conferma: t.inviaPromemoria },
  };
  const azionePrimaria: Azione | null = w.stato === 'bozza' ? 'pubblica' : w.stato === 'pubblicato' && !w.invitiInviatiAt ? 'inviti/invia' : null;

  return (
    <>
      <IntestazioneAdmin
        sopra={<LinkIndietro to={`/admin/percorsi/${w.percorsoId}`} testo={w.percorsoTitolo} />}
        titolo={
          <span className="inline-flex flex-wrap items-center gap-3">
            {w.titolo} <BadgeWebinar stato={w.stato} />
          </span>
        }
        sottotitolo={formattaIntervallo(w.inizioPrevisto, w.finePrevista)}
        azione={
          <div className="flex flex-wrap gap-3">
            <Button icon={Pencil} onClick={() => navigate(`/admin/webinar/${w.id}/modifica`)}>
              {admin.comuni.modifica}
            </Button>
            {w.stato !== 'bozza' && (
              <Button icon={Radio} onClick={() => navigate(`/admin/webinar/${w.id}/diretta`)}>
                {aula.diretta.apri}
              </Button>
            )}
            {azionePrimaria && (
              // Nella scheda Test il primario della schermata è "Salva le domande": qui si passa a secondario.
              <Button variant={scheda === 'test' || scheda === 'report' ? 'secondary' : 'primary'} icon={azionePrimaria === 'pubblica' ? Send : Bell} onClick={() => setDialogo(azionePrimaria)}>
                {dialoghi[azionePrimaria].conferma}
              </Button>
            )}
          </div>
        }
      />
      <Tabs
        ariaLabel={w.titolo}
        value={scheda}
        onValueChange={(v) => setParams({ scheda: v }, { replace: true })}
        aiuto={<HelpButton variant="compatto" label={manualeTesti.apri} onClick={() => manualeCtx.apri()} />}
        items={[
          { value: 'riepilogo', label: t.schede.riepilogo },
          { value: 'conferme', label: t.schede.conferme, count: totaleIscritti },
          { value: 'checklist', label: t.schede.checklist },
          { value: 'test', label: tt.admin.scheda },
          { value: 'attestati', label: td.admin.attestati.scheda },
          { value: 'report', label: td.admin.report.scheda },
        ]}
      >
        <TabPanel value="riepilogo">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="flex flex-col gap-2">
              <h2 className="t-label text-ink-muted">{t.quando}</h2>
              <p className="t-body">{formattaIntervallo(w.inizioPrevisto, w.finePrevista)}</p>
              <p className="t-body-small text-ink-muted">{t.durata(w.durataUfficialeMin)}</p>
              {w.descrizione && <p className="t-body pt-2">{w.descrizione}</p>}
            </Card>
            <Card className="flex flex-col gap-2">
              <h2 className="t-label text-ink-muted">{t.relatori}</h2>
              {w.relatori.length === 0 ? (
                <p className="t-body text-ink-muted">{t.nessunRelatore}</p>
              ) : (
                <ul className="t-body">
                  {w.relatori.map((r) => (
                    <li key={r.id}>
                      {r.nome} {r.cognome}
                    </li>
                  ))}
                </ul>
              )}
              <h2 className="t-label text-ink-muted pt-3">{t.ingresso}</h2>
              <p className="t-body">{admin.modalitaIngresso[w.modalitaIngresso]}</p>
            </Card>
            {terminato && riepilogoPresenze.data && (
              <Card className="flex flex-col gap-3 md:col-span-2">
                <h2 className="t-label text-ink-muted">{tp.admin.titolo}</h2>
                <StatRow>
                  <StatBlock label={tp.admin.riepilogo.totaleConfermati} value={riepilogoPresenze.data.totaleConfermati} />
                  <StatBlock label={tp.admin.riepilogo.sopraSoglia} value={riepilogoPresenze.data.sopraSoglia} tone="success" />
                  <StatBlock label={tp.admin.riepilogo.incoerenze} value={riepilogoPresenze.data.incoerenze} tone={riepilogoPresenze.data.incoerenze > 0 ? 'warning' : 'neutral'} />
                </StatRow>
                <Button variant="discreet" onClick={() => navigate(`/admin/webinar/${w.id}/diretta?scheda=presenze`)} className="self-start">
                  {tp.admin.vaiPresenze}
                </Button>
              </Card>
            )}
            <Card className="flex flex-col gap-3 md:col-span-2">
              <h2 className="t-label text-ink-muted">{t.conteggi}</h2>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  [t.invitati, w.conteggi.invitati],
                  [t.online, w.conteggi.confermatiOnline],
                  [t.sala, w.conteggi.confermatiSala],
                  [t.rinunciati, w.conteggi.rinunciati],
                ].map(([k, v]) => (
                  <div key={String(k)} className="flex flex-col">
                    <dt className="t-body-small text-ink-muted">{k}</dt>
                    <dd className="t-title-2">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-col gap-2 pt-2">
                <p className="t-body-small text-ink-muted inline-flex items-center gap-2">
                  <Send aria-hidden size={16} strokeWidth={1.75} />
                  {w.invitiInviatiAt ? t.invitiInviati(formattaDataOra(w.invitiInviatiAt)) : t.invitiAutomatici}
                </p>
                <p className="t-body-small text-ink-muted inline-flex items-center gap-2">
                  <Bell aria-hidden size={16} strokeWidth={1.75} />
                  {w.promemoriaInviatoAt ? t.promemoriaInviato(formattaDataOra(w.promemoriaInviatoAt)) : t.promemoriaAutomatico}
                </p>
                {w.stato === 'pubblicato' && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    {w.invitiInviatiAt && (
                      <Button icon={Send} onClick={() => setDialogo('inviti/invia')}>
                        {t.inviaInviti}
                      </Button>
                    )}
                    {w.invitiInviatiAt && (
                      <Button icon={Bell} onClick={() => setDialogo('promemoria/invia')}>
                        {t.inviaPromemoria}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </TabPanel>
        <TabPanel value="conferme">
          <SchedaConferme webinarId={w.id} />
        </TabPanel>
        <TabPanel value="checklist">
          <SchedaChecklist webinarId={w.id} />
        </TabPanel>
        <TabPanel value="test">
          <SchedaTest webinarId={w.id} terminato={terminato} />
        </TabPanel>
        <TabPanel value="attestati">
          <SchedaAttestati webinarId={w.id} />
        </TabPanel>
        <TabPanel value="report">
          <SchedaReport webinarId={w.id} />
        </TabPanel>
      </Tabs>
      <Dialog
        open={dialogo !== null}
        onOpenChange={(o) => !o && setDialogo(null)}
        title={dialogo ? dialoghi[dialogo].titolo : ''}
        description={dialogo ? dialoghi[dialogo].testo : ''}
        actions={
          <>
            <Button onClick={() => setDialogo(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={() => dialogo && esegui(dialogo)} loading={azione.isPending} loadingText={admin.comuni.inCorso}>
              {dialogo ? dialoghi[dialogo].conferma : ''}
            </Button>
          </>
        }
      />
    </>
  );
}

function SchedaConferme({ webinarId }: { webinarId: string }) {
  const t = admin.webinar.dettaglio.conferme;
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [pagina, setPagina] = useState(1);
  const conferme = useConferme(webinarId, { q: q || undefined, stato: stato || undefined, pagina, perPagina: 50 });
  const colonne = useMemo<ColumnDef<ConfermaAdmin, unknown>[]>(
    () => [
      { header: t.colonne.nome, cell: ({ row }) => `${row.original.cognome} ${row.original.nome}` },
      { header: t.colonne.email, accessorKey: 'email' },
      { header: t.colonne.stato, cell: ({ row }) => <BadgeConferma stato={row.original.stato} /> },
      { header: t.colonne.modalita, cell: ({ row }) => (row.original.modalita ? admin.modalita[row.original.modalita] : '—') },
      { header: t.colonne.quando, cell: ({ row }) => (row.original.confermataAt ? formattaDataOra(row.original.confermataAt) : '—') },
    ],
    [t],
  );
  const vuoto = !q && !stato;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} hint={admin.comuni.cercaAiuto} />
        <NativeSelect
          label={admin.comuni.filtroStato}
          placeholder={admin.comuni.tutti}
          value={stato}
          onChange={(e) => { setStato(e.target.value); setPagina(1); }}
          options={Object.entries(admin.statiConferma).map(([value, label]) => ({ value, label }))}
        />
      </div>
      {conferme.isError && <Alert variant="error" title={conferme.error.message} />}
      <DataTable
        caption={admin.webinar.dettaglio.schede.conferme}
        columns={colonne}
        data={conferme.data?.elementi ?? []}
        getRowId={(c) => c.confermaId}
        loading={conferme.isPending}
        testi={admin.comuni}
        pagination={conferme.data ? { pagina: conferme.data.pagina, perPagina: conferme.data.perPagina, totale: conferme.data.totale, onPagina: setPagina } : undefined}
        empty={<EmptyState icon={Users} title={vuoto ? t.nessuna : t.nessunRisultato} />}
      />
    </>
  );
}

function SchedaChecklist({ webinarId }: { webinarId: string }) {
  const t = admin.webinar.dettaglio.checklist;
  const voci = useChecklist(webinarId);
  const aggiorna = useAggiornaVoceChecklist(webinarId);
  const toast = useToast();
  const [noteAperte, setNoteAperte] = useState<VoceChecklist | null>(null);
  const [testoNote, setTestoNote] = useState('');

  const gruppi = useMemo(() => {
    const ordine = Object.keys(t.momenti);
    const m = new Map<string, VoceChecklist[]>();
    for (const v of voci.data ?? []) m.set(v.momento, [...(m.get(v.momento) ?? []), v]);
    return ordine.filter((k) => m.has(k)).map((k) => ({ momento: k as keyof typeof t.momenti, voci: (m.get(k) ?? []).sort((a, b) => a.ordine - b.ordine) }));
  }, [voci.data, t]);

  async function cambia(v: VoceChecklist, stato: 'da_fare' | 'fatta' | 'non_applicabile', note?: string) {
    try {
      await aggiorna.mutateAsync({ voceId: v.id, stato, note: note ?? v.note ?? '' });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  if (voci.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (voci.isError) return <Alert variant="error" title={voci.error.message} />;
  if (voci.data.length === 0) return <EmptyState icon={ClipboardList} title={t.nessuna} />;

  return (
    <>
      <p className="t-body text-ink-muted">
        {t.sottotitolo}{' '}
        <Link to={`/admin/webinar/${webinarId}?scheda=test`} className="t-label">
          {tt.admin.vaiAlTest}
        </Link>
      </p>
      {gruppi.map((g) => (
        <section key={g.momento} aria-labelledby={`momento-${g.momento}`} className="flex flex-col gap-2">
          <h3 id={`momento-${g.momento}`} className="t-title-3">
            {t.momenti[g.momento]}
          </h3>
          <Card className="flex flex-col divide-y divide-border p-0">
            {g.voci.map((v) => (
              <div key={v.id} className="flex flex-col sm:flex-row sm:items-start gap-2 px-6 py-2">
                <div className="flex-1">
                  {v.automatica ? (
                    <div className="flex items-start gap-3 min-h-[44px] py-2.5">
                      {v.stato === 'automatica_ok' ? (
                        <CircleCheck aria-hidden size={24} strokeWidth={1.75} className="text-success shrink-0" />
                      ) : v.stato === 'automatica_errore' ? (
                        <CircleAlert aria-hidden size={24} strokeWidth={1.75} className="text-error shrink-0" />
                      ) : (
                        <ClipboardList aria-hidden size={24} strokeWidth={1.75} className="text-ink-muted shrink-0" />
                      )}
                      <div className="flex flex-col gap-0.5">
                        <span className="t-body">{v.titolo}</span>
                        <Badge variant={v.stato === 'automatica_ok' ? 'success' : v.stato === 'automatica_errore' ? 'error' : 'neutral'}>{t.stati[v.stato]}</Badge>
                      </div>
                    </div>
                  ) : (
                    <Checkbox
                      checked={v.stato === 'fatta'}
                      disabled={aggiorna.isPending}
                      onCheckedChange={(c) => cambia(v, c ? 'fatta' : 'da_fare')}
                      label={<span className={v.stato === 'non_applicabile' ? 'line-through text-ink-muted' : undefined}>{v.titolo}</span>}
                      hint={
                        <>
                          {v.stato === 'fatta' && v.fattaDa && v.fattaAt ? t.fattaDa(v.fattaDa, formattaDataOra(v.fattaAt)) : v.stato === 'non_applicabile' ? t.stati.non_applicabile : null}
                          {v.note ? ` · ${v.note}` : ''}
                        </>
                      }
                    />
                  )}
                </div>
                {!v.automatica && (
                  <div className="flex gap-2 sm:pt-1">
                    <Button variant="discreet" onClick={() => { setNoteAperte(v); setTestoNote(v.note ?? ''); }}>
                      {t.note}
                    </Button>
                    {v.stato === 'non_applicabile' ? (
                      <Button variant="discreet" onClick={() => cambia(v, 'da_fare')}>{t.riapri}</Button>
                    ) : (
                      <Button variant="discreet" onClick={() => cambia(v, 'non_applicabile')}>{t.nonApplicabile}</Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Card>
        </section>
      ))}
      <Dialog
        open={noteAperte !== null}
        onOpenChange={(o) => !o && setNoteAperte(null)}
        title={noteAperte?.titolo ?? ''}
        actions={
          <>
            <Button onClick={() => setNoteAperte(null)}>{admin.comuni.annulla}</Button>
            <Button
              variant="primary"
              loading={aggiorna.isPending}
              loadingText={admin.comuni.inCorso}
              onClick={async () => {
                if (!noteAperte) return;
                const stato = noteAperte.stato === 'fatta' || noteAperte.stato === 'non_applicabile' ? noteAperte.stato : 'da_fare';
                await cambia(noteAperte, stato, testoNote);
                setNoteAperte(null);
              }}
            >
              {admin.comuni.salva}
            </Button>
          </>
        }
      >
        <TextArea label={t.note} value={testoNote} onChange={(e) => setTestoNote(e.target.value)} maxLength={1000} />
      </Dialog>
    </>
  );
}

